import React, { useCallback, useEffect, useState } from 'react';
import {
  runnerApi, errorText,
  type ApprovalAction, type ApprovalRequest, type OpsSafety, type SafetyInfo,
} from '../../../api/migrationRunner';
import { Alert, Btn, ErrorBox, Panel, Pill, Spinner, when } from './ui';

/**
 * Safety (approved mockup "Database redesign": Second approval + Maintenance
 * banner). Requests waiting for a second owner (Approve / Decline — never
 * your own), what needs two people, and the "Updating the system" notice.
 */

const RULE_ORDER: ApprovalAction[] = ['restore_replace', 'console_write', 'batch_all_companies', 'company_data_delete'];

function minutesLeft(iso: string): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60_000));
}

const STATUS_TONE: Record<string, 'ok' | 'bad' | 'warn' | 'mut' | 'info'> = {
  pending: 'warn', approved: 'ok', declined: 'bad', expired: 'mut', used: 'mut', cancelled: 'mut',
};

const SafetyTab: React.FC<{ isOwner: boolean; onPendingCount?: (n: number) => void }> = ({ isOwner, onPendingCount }) => {
  const [info, setInfo] = useState<SafetyInfo | null>(null);
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [recent, setRecent] = useState<ApprovalRequest[]>([]);
  const [me, setMe] = useState('');
  const [draft, setDraft] = useState<OpsSafety | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, open, all] = await Promise.all([runnerApi.safety(), runnerApi.approvals('open'), runnerApi.approvals()]);
      setInfo(s);
      setDraft((d) => d ?? s.settings);
      setRequests(open.approvals);
      setRecent(all.approvals.filter((a) => !['pending', 'approved'].includes(a.status)).slice(0, 10));
      setMe(open.me);
      onPendingCount?.(open.approvals.filter((a) => a.status === 'pending' && a.requestedBy.toLowerCase() !== open.me.toLowerCase()).length);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load the safety settings'));
    }
  }, [onPendingCount]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const decide = async (id: string, decision: 'approve' | 'decline') => {
    setBusy(id);
    try { await runnerApi.decideApproval(id, decision, notes[id]?.trim() || undefined); await load(); } catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  };
  const withdraw = async (id: string) => {
    setBusy(id);
    try { await runnerApi.cancelApproval(id); await load(); } catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  };
  const save = async () => {
    if (!draft) return;
    setBusy('save');
    try { setDraft(await runnerApi.saveSafety(draft)); setSaved(true); setTimeout(() => setSaved(false), 2500); await load(); } catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  };

  if (!info) {
    return <div className="px-6 py-6">{error ? <ErrorBox>{error}</ErrorBox> : <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Loading…</div>}</div>;
  }
  const mine = (a: ApprovalRequest) => a.requestedBy.toLowerCase() === me.toLowerCase();
  const waiting = requests.filter((r) => r.status === 'pending');
  const dirty = draft && JSON.stringify(draft) !== JSON.stringify(info.settings);

  return (
    <div className="space-y-4 px-6 py-6">
      {error && <ErrorBox>{error}</ErrorBox>}
      {!info.schema.approvals && (
        <Alert tone="warn" title="Second approval is not set up on this registry">
          Apply migrations-2026-10-11-ops-approvals (Status tab, registry row). Until then these actions run without a second owner.
        </Alert>
      )}

      <Panel
        title="Waiting for a second owner"
        actions={<Pill tone={waiting.length ? 'warn' : 'mut'}>{waiting.length} request{waiting.length === 1 ? '' : 's'}</Pill>}
      >
        <div className="space-y-3">
          {requests.length === 0 && <p className="text-[13px] text-slate-500">Nothing waiting.</p>}
          {requests.map((r) => (
            <div key={r.id} className="rounded-xl border-[1.5px] border-slate-200 px-3.5 py-3 text-[13px]">
              <div className="flex flex-wrap items-center gap-2">
                <b className="text-slate-900">{r.summary || r.actionLabel}</b>
                <Pill tone={STATUS_TONE[r.status]}>{r.status === 'approved' ? `approved by ${r.approvedBy}` : r.status}</Pill>
              </div>
              <div className="mt-0.5 text-slate-500">Asked by {r.requestedBy} · {when(r.createdAt)} · reason: "{r.reason}"</div>
              {r.action === 'console_write' && typeof r.payload.query === 'string' && (
                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-2 font-mono text-[12px] text-slate-800">{String(r.payload.target)}: {r.payload.query}</pre>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {r.status === 'pending' && !mine(r) && isOwner && (
                  <>
                    <input
                      value={notes[r.id] ?? ''}
                      onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                      placeholder="Note (optional)"
                      className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13px]"
                    />
                    <Btn kind="success" small disabled={busy === r.id} onClick={() => decide(r.id, 'approve')}>Approve</Btn>
                    <Btn kind="ghost" small disabled={busy === r.id} onClick={() => decide(r.id, 'decline')}>Decline</Btn>
                  </>
                )}
                {r.status === 'pending' && mine(r) && <span className="text-xs text-slate-500">Yours: another owner has to decide.</span>}
                {mine(r) && <Btn kind="ghost" small disabled={busy === r.id} onClick={() => withdraw(r.id)}>Withdraw</Btn>}
                {r.status === 'approved' && mine(r) && <span className="text-xs text-green-700">Approved: do the action again to use it.</span>}
                <span className="ml-auto text-xs text-slate-500">Expires in {minutesLeft(r.expiresAt)} min</span>
              </div>
            </div>
          ))}
          {info.otherOwners.length === 0 && (
            <p className="text-xs text-amber-800">You're the only owner with database access, so nobody else can approve your requests.</p>
          )}
        </div>
      </Panel>

      <Panel title="What needs two people" actions={!isOwner ? <Pill tone="mut">owners change these</Pill> : undefined}>
        <div className="divide-y divide-slate-100 text-[13.5px]">
          {draft && RULE_ORDER.map((action) => (
            <div key={action} className="flex flex-wrap items-center gap-3 py-2.5">
              <span className="flex-1 text-slate-800">
                {info.actions[action]}
                {action === 'company_data_delete' && <small className="block text-xs text-slate-500">Applies when deleting a company's data from the Database page.</small>}
                {action === 'batch_all_companies' && <small className="block text-xs text-slate-500">A batch whose targets are every company database.</small>}
              </span>
              <select
                value={draft.approvals[action]}
                disabled={!isOwner}
                onChange={(e) => setDraft({ ...draft, approvals: { ...draft.approvals, [action]: e.target.value as 'always' | 'optional' } })}
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[13px]"
              >
                <option value="always">Always</option>
                <option value="optional">Optional</option>
              </select>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">An approval is for that exact action (same backup, same statement), used once, and expires after an hour. Nobody approves their own.</p>
      </Panel>

      <Panel title="Maintenance notice">
        <div className="space-y-3 text-[13.5px]">
          <p className="text-slate-500">While a company's database is being migrated (or restored over live), its people see a short notice instead of errors. Phones keep saving to the phone and send when it's done.</p>
          <div className="flex flex-wrap items-start gap-4">
            <div className="w-full max-w-[320px] overflow-hidden rounded-[22px] border border-slate-200 bg-[#f7f5f1] text-[#1c1917]">
              <div className="bg-[#ffd400] px-3.5 py-2.5 text-[13px] font-extrabold text-[#1f1c19]">Updating the system · back in about a minute. Your work is saved on this phone.</div>
              <div className="px-3.5 py-3 text-[13px] text-[#78716c]">Crew app keeps working offline.</div>
            </div>
            <div className="min-w-0 flex-1 overflow-hidden rounded-xl border border-slate-200">
              <div className="bg-amber-100 px-3.5 py-2.5 text-[13px] font-bold text-amber-800">Scheduled update in progress (started 11:02 PM). Saving is paused for a minute.</div>
              <div className="px-3.5 py-3 text-[13px] text-slate-500">Admin portal</div>
            </div>
          </div>
          {draft && (
            <label className="flex items-center gap-2">
              <input type="checkbox" disabled={!isOwner} checked={draft.maintenance_banner} onChange={(e) => setDraft({ ...draft, maintenance_banner: e.target.checked })} />
              Show the notice automatically during runs and restores
            </label>
          )}
          {!info.schema.maintenance && <p className="text-xs text-amber-800">Needs migrations-2026-10-11-ops-approvals on the registry.</p>}
          <div>
            <b className="text-slate-900">Showing it now</b>
            {info.maintenance.length === 0 && <span className="ml-2 text-slate-500">no company</span>}
            <ul className="mt-1 space-y-1">
              {info.maintenance.map((m) => (
                <li key={m.companyId} className="flex flex-wrap items-center gap-2">
                  <b>{m.companyName}</b>
                  <Pill tone="warn">{m.reason === 'restore' ? 'restoring' : 'migrating'}</Pill>
                  <span className="text-xs text-slate-500">since {when(m.startedAt)} · clears itself by {when(m.expiresAt)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Panel>

      {isOwner && (
        <div className="flex items-center gap-3">
          <Btn onClick={save} disabled={!dirty || busy === 'save'}>{busy === 'save' ? 'Saving…' : 'Save'}</Btn>
          {saved && <span className="text-sm text-green-700">Saved</span>}
        </div>
      )}

      {recent.length > 0 && (
        <Panel title="Recent decisions" bodyClass="divide-y divide-slate-100">
          {recent.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 px-4 py-2 text-[13px]">
              <Pill tone={STATUS_TONE[r.status]}>{r.status}</Pill>
              <span className="min-w-0 flex-1 text-slate-700">{r.summary || r.actionLabel}</span>
              <span className="text-xs text-slate-500">{r.requestedBy}{r.approvedBy ? ` · ${r.approvedBy}` : ''} · {when(r.decidedAt || r.createdAt)}</span>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
};

export default SafetyTab;
